import { fetchChat } from "@/libs/api/requests";
import { useDriver } from "@/libs/auth/data/driver";
import { useCallback, useEffect, useState } from "react";
import { CHAT_MESSAGES_PAGINATION_LIMIT } from "../constants";
import { useChatContext } from "./useChatContext";

export function useChat(id?: string) {
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { currentConversation, conversations, loading, error, setContext } =
    useChatContext();

  const { driver } = useDriver();

  const handleLoadMore = useCallback(() => {
    if (!id || !currentConversation || isLoadingMore || !hasMoreMessages) {
      return;
    }

    setIsLoadingMore(true);
    const offset = currentConversation.messages.length;

    fetchChat({
      conversationId: id,
      limit: CHAT_MESSAGES_PAGINATION_LIMIT,
      offset,
    }).then((data) => {
      if (!data) {
        return;
      }

      setContext({
        type: "appendMessages",
        payload: data.items,
      });

      setHasMoreMessages(data.items.length >= CHAT_MESSAGES_PAGINATION_LIMIT);
      setIsLoadingMore(false);
    });
  }, [currentConversation, hasMoreMessages, id, isLoadingMore, setContext]);

  useEffect(() => {
    if (
      !id ||
      (currentConversation && currentConversation.conversationId === id)
    ) {
      return;
    }

    setContext({ type: "loading", payload: true });

    fetchChat({
      conversationId: id,
      limit: CHAT_MESSAGES_PAGINATION_LIMIT,
      offset: 0,
    })
      .then((data) => {
        if (!data || !data.items) {
          return;
        }

        const conversationData = conversations.get(id);
        const participants = conversationData?.participants;

        if (!driver || !driver.employeeId) {
          return;
        }

        setContext({
          type: "setCurrentConversation",
          payload: {
            conversationId: id,
            messages: data.items,
            senderId: driver?.employeeId,
            participants: participants || [],
          },
        });

        setHasMoreMessages(data.items.length >= CHAT_MESSAGES_PAGINATION_LIMIT);
      })
      .catch((err) => {
        setContext({ type: "error", payload: err.message });
        console.log("Error fetching chat:", err);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return {
    conversation: currentConversation,
    currentUserId: driver?.employeeId,
    loading,
    error,
    handleLoadMore,
    hasMoreMessages,
    isLoadingMore,
    conversations,
  };
}
